import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AreaLayout } from "@/pages/area/AreaLayout";
import { AppIcon, ICON_OPTIONS } from "@/components/AppIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const COLORS = ["#2563EB", "#10B981", "#F97316", "#8B5CF6", "#EF4444", "#06B6D4", "#EAB308"];
const empty = { client_name: "", title: "", description: "", icon: "Lock", color: "#8B5CF6", content: "", external_url: "", permissions: [], active: true };

export default function AreaGestione() {
  const [areas, setAreas] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);

  const load = () => {
    api.get("/area/areas").then(({ data }) => setAreas(data));
    api.get("/users").then(({ data }) => setUsers(data.filter((u) => u.role !== "admin")));
  };
  useEffect(load, []);

  const openNew = () => { setF({ ...empty }); setOpen(true); };
  const openEdit = (a) => { setF({ ...a }); setOpen(true); };

  const save = async () => {
    const payload = { ...f }; const aid = payload.area_id; delete payload.area_id; delete payload.created_at;
    try {
      if (aid) await api.put(`/area/areas/${aid}`, payload); else await api.post("/area/areas", payload);
      toast.success("Area salvata"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (a) => {
    try { await api.delete(`/area/areas/${a.area_id}`); toast.success("Area eliminata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const togglePerm = (uid, checked) => {
    const set = new Set(f.permissions || []);
    if (checked) set.add(uid); else set.delete(uid);
    setF({ ...f, permissions: Array.from(set) });
  };

  return (
    <AreaLayout
      subtitle="Amministrazione"
      title="Gestione aree riservate"
      actions={<Button data-testid="area-add-btn" onClick={openNew} className="brand-gradient text-white border-0"><Plus className="h-4 w-4 mr-2" /> Nuova area</Button>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {areas.map((a) => (
          <div key={a.area_id} data-testid={`area-admin-${a.area_id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between mb-2">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${a.color}1A`, color: a.color }}>
                <AppIcon name={a.icon} className="h-5 w-5" />
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)} data-testid={`area-edit-${a.area_id}`}><Pencil className="h-4 w-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50" data-testid={`area-delete-${a.area_id}`}><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Eliminare "{a.title}"?</AlertDialogTitle>
                      <AlertDialogDescription>L'area e i suoi accessi verranno rimossi.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(a)} className="bg-red-600 hover:bg-red-700 text-white">Elimina</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-slate-400">{a.client_name}</p>
            <h4 className="font-heading font-medium text-slate-900">{a.title}</h4>
            <p className="text-sm text-slate-500 line-clamp-2 mt-1">{a.description}</p>
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">{(a.permissions || []).length} utenti abilitati</Badge>
              {!a.active && <Badge variant="outline" className="text-[10px] text-red-500 border-red-200">Disattiva</Badge>}
            </div>
          </div>
        ))}
      </div>

      {f && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{f.area_id ? "Modifica" : "Nuova"} area riservata</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5"><Label className="text-slate-600 text-sm">Cliente / Convenzione</Label><Input data-testid="area-client-input" value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} placeholder="Es. Anffas APS" /></div>
              <div className="space-y-1.5"><Label className="text-slate-600 text-sm">Titolo area</Label><Input data-testid="area-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Es. Archivio polizze" /></div>
              <div className="space-y-1.5"><Label className="text-slate-600 text-sm">Descrizione</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-slate-600 text-sm">Icona</Label>
                  <Select value={f.icon} onValueChange={(v) => setF({ ...f, icon: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">{ICON_OPTIONS.map((ic) => <SelectItem key={ic} value={ic}><span className="flex items-center gap-2"><AppIcon name={ic} className="h-4 w-4" /> {ic}</span></SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-600 text-sm">Colore</Label>
                  <div className="flex gap-2 flex-wrap pt-1">
                    {COLORS.map((c) => <button key={c} type="button" onClick={() => setF({ ...f, color: c })} className={`h-7 w-7 rounded-full border-2 ${f.color === c ? "border-slate-900 scale-110" : "border-transparent"}`} style={{ backgroundColor: c }} />)}
                  </div>
                </div>
              </div>
              <div className="space-y-1.5"><Label className="text-slate-600 text-sm">URL app/archivio esterno (opzionale)</Label><Input value={f.external_url} onChange={(e) => setF({ ...f, external_url: e.target.value })} placeholder="https://…" /></div>
              <div className="space-y-1.5"><Label className="text-slate-600 text-sm">Contenuto / specifiche (opzionale, da alimentare)</Label><Textarea rows={3} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label className="text-slate-600 text-sm">Utenti abilitati</Label>
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-40 overflow-y-auto">
                  {users.length === 0 ? <p className="text-sm text-slate-400 p-3">Nessun utente non-admin. Creane dal pannello Admin della Suite.</p> :
                    users.map((u) => (
                      <label key={u.user_id} data-testid={`area-perm-${u.user_id}`} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-slate-50">
                        <Checkbox checked={(f.permissions || []).includes(u.user_id)} onCheckedChange={(c) => togglePerm(u.user_id, c)} className="data-[state=checked]:bg-brand-blue data-[state=checked]:border-brand-blue" />
                        <span className="text-sm text-slate-900">{u.name}</span>
                        <span className="text-xs text-slate-400">{u.email}</span>
                      </label>
                    ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Area attiva</label>
            </div>
            <DialogFooter><Button data-testid="area-save-btn" onClick={save} disabled={!f.client_name || !f.title} className="brand-gradient text-white border-0">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AreaLayout>
  );
}
